import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { omitPendingParam, splitRouteParams } from '@aline/common';
import { keys, omit } from 'ramda';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import { createHotkeys } from 'src/helpers/hotkeys';
import { useDispatchNotification } from 'src/helpers/snackbars';
import { OPEN_IN_APP_TAB_EVENT } from 'src/helpers/url';
import { v4 } from 'uuid';

import { applyIncomingRoute, getRouteWithStickyParams } from './apply-incoming-route';
import { useCurrentTabId } from './tab-active-context';
import { getRoutesMatchSameResource } from './tab-route-matching';
import type { Tab, TabsSnapshot, TabsState, TabType } from './tab-types';
import {
  createTabsSnapshot,
  DEFAULT_TAB_ROUTE,
  getTabInfoFromRoute,
  readTabIdFromHistory,
  readTabsSnapshotFromHistory,
  TAB_TYPES,
  tabsStateForPersist,
} from './tab-types';

const ONE_THROUGH_EIGHT = [1, 2, 3, 4, 5, 6, 7, 8];

export const tabHotkeys = createHotkeys<
  {
    event: KeyboardEvent;
    state: TabsState;
  } & Pick<TabContextValue['internalMethods'], 'openNewTab' | 'closeTab' | 'switchToTab'>
>()({
  /** Close the active tab (unless pinned). */
  closeTab: {
    action: ({ closeTab, state: { activeTabId, tabs } }) => {
      if (!activeTabId) return;
      const active = tabs.find(t => t.id === activeTabId);
      if (active?.pinned) return;
      closeTab(activeTabId);
    },
    apple: { hint: '^⌘W', key: 'meta+control+w' },
    pc: { hint: 'Ctrl+Alt+W', key: 'control+alt+w' },
  },

  /** Switch to the next tab. */
  nextTab: {
    action: ({ state: { activeTabId, tabs }, switchToTab }) => {
      const activeIndex = tabs.findIndex(t => t.id === activeTabId);
      if (activeIndex < tabs.length - 1) switchToTab(tabs[activeIndex + 1].id);
    },
    apple: { hint: '^⌘]', key: 'meta+control+]' },
    pc: { hint: 'Ctrl+Alt+]', key: 'control+alt+]' },
  },

  /** Open a new tab. */
  openNewTab: {
    action: ({ openNewTab }) => openNewTab(TAB_TYPES.home, 'Home', DEFAULT_TAB_ROUTE),
    apple: { hint: '^⌘T', key: 'meta+control+t' },
    pc: { hint: 'Ctrl+Alt+T', key: 'control+alt+t' },
  },

  /** Switch to the previous tab. */
  previousTab: {
    action: ({ state: { activeTabId, tabs }, switchToTab }) => {
      const activeIndex = tabs.findIndex(t => t.id === activeTabId);
      if (activeIndex > 0) switchToTab(tabs[activeIndex - 1].id);
    },
    apple: { hint: '^⌘[', key: 'meta+control+[' },
    pc: { hint: 'Ctrl+Alt+[', key: 'control+alt+[' },
  },

  /** Switch to the last tab. */
  switchToLastTab: {
    action: ({ state: { tabs }, switchToTab }) => {
      const targetTab = tabs.at(-1);
      if (targetTab) switchToTab(targetTab.id);
    },
    apple: { hint: '^⌘9', key: 'meta+control+9' },
    pc: { hint: 'Ctrl+Alt+9', key: 'control+alt+9' },
  },

  /** Switch to a specific tab. Make sure to replace '<N>' with the correct digit in the hint. */
  switchToTab: {
    action: ({ event, state: { tabs }, switchToTab }) => {
      const digit = parseInt(event.key, 10);
      const targetTab = tabs.at(digit - 1);
      if (targetTab) switchToTab(targetTab.id);
    },
    apple: { hint: '^⌘<N>', key: ONE_THROUGH_EIGHT.map(digit => `meta+control+${digit}`) },
    pc: { hint: 'Ctrl+Alt+<N>', key: ONE_THROUGH_EIGHT.map(digit => `control+alt+${digit}`) },
  },
});

const tabHotkeyKeys = keys(tabHotkeys);

export interface OpenInAppTabOptions {
  route: string;
  /**
   * When `preferExistingTab` switches to an already-open tab, also navigate that tab to `route`
   * instead of leaving it on its stored route. Required by deep links whose URL params must reach
   * the target tab (e.g. citation scroll params) — without it the params are silently dropped.
   * @default false
   */
  applyRouteToExistingTab?: boolean;
  /**
   * Whether to open the route in a new tab.
   * @default true
   */
  newTab?: boolean;
  /**
   * Whether to switch to an existing tab with a matching route if one exists.
   * @default true
   */
  preferExistingTab?: boolean;
  /**
   * For internal routes, used as the in-app tab title (e.g. document or workflow name) so the new
   * tab has a meaningful label immediately. For external URLs, passed to `window.open` as the
   * window target name (so reusing the same `title` reuses the same window).
   */
  title?: string;
}

// ---------------------------------------------------------------------------
// Route helpers
// ---------------------------------------------------------------------------

/**
 * Serializes a location (or the browser's current location) into the full route
 * string stored on a tab. Must include search + hash so that query-param state
 * (e.g. `?walkThrough=true`, `?fieldId=...`) survives into the tab's isolated
 * router context — otherwise URL-driven features like the walkthrough and field
 * deep-links break inside tabs.
 */
function routeFromLocation(
  location:
    | Pick<Location, 'pathname' | 'search' | 'hash'>
    | { pathname: string; hash?: string; search?: string }
): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

function currentBrowserRoute(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

interface TabContextValue {
  activeTab: Tab | undefined;
  /**
   * Reads and clears one pending deep-link param from the given tab. Returns null when absent.
   */
  consumePendingParam: (tabId: UUID, key: string) => string | null;
  /**
   * Internal methods that should typically be called only from tab-related code such as the TabBar
   * component.
   * @private
   */
  internalMethods: {
    closeOtherTabs: (tabId: UUID) => void;
    closeTab: (tabId: UUID) => void;
    closeTabsToLeft: (tabId: UUID) => void;
    closeTabsToRight: (tabId: UUID) => void;
    /** Prefer openLink */
    navigateInTab: (type: TabType, title: string, route: string) => void;
    /** Prefer openLink */
    openNewTab: (type: TabType, title: string, route: string) => void;
    reorderTab: (fromIndex: number, toIndex: number) => void;
    setTabOrder: (orderedIds: UUID[]) => void;
    switchToTab: (tabId: UUID, routeOverride?: string) => void;
    togglePinTab: (tabId: UUID) => void;
  };
  tabs: Tab[];
  updateActiveTabTitle: (title: string) => void;
}

const TabContext = createContext<TabContextValue | null>(null);

/**
 * Closes the tab this component renders in. Null outside the tabbed app, as on a guest's task page.
 */
export function useCloseCurrentTab(): (() => void) | null {
  const context = useContext(TabContext);
  const tabId = useCurrentTabId();
  const closeTab = context?.internalMethods.closeTab;
  return useMemo(() => (closeTab && tabId ? () => closeTab(tabId) : null), [closeTab, tabId]);
}

export function useTabContext(): TabContextValue {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within TabProvider');
  }
  return context;
}

const DEFAULT_STATE: TabsState = {
  activeTabId: null,
  tabs: [],
};

// ---------------------------------------------------------------------------
// Immutable tab-state helpers
// ---------------------------------------------------------------------------

function updateTab(tabs: Tab[], tabId: UUID, updates: Partial<Tab>): Tab[] {
  return tabs.map(t => (t.id === tabId ? { ...t, ...updates } : t));
}

/**
 * The type and title a tab takes when it moves from `prevPathname` to `pathname`. The title resets
 * to the new route's default when it is missing, when it was the previous route's default, or when
 * the tab's type changes. Otherwise it is kept, since pages like the document editor and parties
 * set custom titles via `updateActiveTabTitle` once their data loads.
 */
function getTabTypeAndTitleForRoute({
  pathname,
  prevPathname,
  tab,
}: {
  pathname: string;
  prevPathname: string;
  tab: Pick<Tab, 'title' | 'type'> | undefined;
}): Pick<Tab, 'title' | 'type'> {
  const info = getTabInfoFromRoute(pathname);
  const titleIsStaleDefault = tab?.title === getTabInfoFromRoute(prevPathname).defaultTitle;
  const typeIsChanging = tab?.type !== info.type;
  if (!tab?.title || titleIsStaleDefault || typeIsChanging)
    return { title: info.defaultTitle || 'New Tab', type: info.type };
  return { title: tab.title, type: info.type };
}

function getPathnameFromRoute(route: string): string {
  return route.split(/[?#]/)[0];
}

// ---------------------------------------------------------------------------
// Browser history snapshot helpers
// ---------------------------------------------------------------------------

/** Returns the state object to pass to `navigate(route, { state })`. */
function createNavigateState(snapshot: TabsSnapshot) {
  return { alineTabsSnapshot: snapshot };
}

/**
 * The live tab model is persisted in sessionStorage. Browser history entries
 * store snapshots under `usr.alineTabsSnapshot` so Back/Forward can restore
 * the tab arrangement that existed when each history entry was created.
 *
 * Tags the current browser history entry with a full tabs snapshot via replaceState.
 * Used as a fallback for navigations not controlled by TabProvider (e.g., direct
 * useNavigate() calls from within tab content). Writes under the `usr` key to
 * match React Router 6's internal structure.
 */
function tagHistoryWithSnapshot(snapshot: TabsSnapshot): void {
  const hs = window.history.state ?? {};
  window.history.replaceState(
    { ...hs, usr: { ...(hs.usr ?? {}), alineTabsSnapshot: snapshot } },
    ''
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function TabProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitializedRef = useRef(false);
  const [persistedState, setPersistedState] = useSessionStorage<TabsState>(
    'aline-tabs',
    DEFAULT_STATE
  );
  const [pendingByTabId, setPendingByTabId] = useState<Record<string, Record<string, string>>>({});
  /**
   * Mirrors `pendingByTabId` for synchronous reads. `consumePendingParam` has to return its value
   * to the caller immediately, so it cannot use a functional setState — and every consumer that
   * fires in the same commit sees the same pre-commit React state. Without this ref, three deep
   * link params arriving together (citation + citationText + highlightRelationship) each write
   * back the OTHER two, so only the last consumer's key is actually removed and the rest stay
   * pending for the life of the tab.
   */
  const pendingByTabIdRef = useRef(pendingByTabId);
  // Desired browser page title — used by the document.title interceptor
  const desiredTitleRef = useRef<string>('Aline');
  // Soft limit warning — show once per session when 20+ tabs
  const tabLimitWarningShownRef = useRef(false);
  const dispatchTabLimitWarning = useDispatchNotification({
    message: 'You have 20+ tabs open. Consider closing some for better performance.',
    variant: 'warning',
  });

  /**
   * Until the session restore below has committed, the persisted active tab may not be the one
   * the URL asks for. Rendering it would mount its pane with the real navigator, and the pane's
   * mount effects would write its old route back to the URL: leaving a signed document for Home
   * landed on the document again. So no tab renders until then. The restore re-renders before
   * the first paint, so nothing flashes.
   */
  const [isRestored, setIsRestored] = useState(false);

  const state = useMemo((): TabsState => {
    if (!isRestored) return DEFAULT_STATE;

    return {
      activeTabId: persistedState.activeTabId,
      tabs: persistedState.tabs.map(tab => {
        const pendingParams = pendingByTabId[tab.id];
        return pendingParams ? { ...tab, pendingParams } : tab;
      }),
    };
  }, [isRestored, pendingByTabId, persistedState]);
  const stateRef = useRef(state);
  stateRef.current = state;

  const commitState = useCallback(
    (newState: TabsState) => {
      const nextPending: Record<string, Record<string, string>> = {};
      for (const tab of newState.tabs) {
        if (tab.pendingParams && Object.keys(tab.pendingParams).length > 0) {
          nextPending[tab.id] = tab.pendingParams;
        }
      }
      pendingByTabIdRef.current = nextPending;
      setPendingByTabId(nextPending);
      setPersistedState(tabsStateForPersist(newState));
    },
    [setPersistedState]
  );

  const commitCurrentHistoryState = useCallback(
    (newState: TabsState) => {
      commitState(newState);
      tagHistoryWithSnapshot(createTabsSnapshot(newState));
    },
    [commitState]
  );

  // -------------------------------------------------------------------------
  // Initialization and session restore
  // -------------------------------------------------------------------------

  /**
   * A layout effect so the active tab exists before the first paint. The shell reads it to
   * decide whether to show the left nav, and a tab-less first frame would flash it in.
   *
   * The URL rewrite waits for the passive effect below: the router subscribes to history in
   * its own layout effect, which runs after this one, so a navigate from here would be lost.
   */
  const initialRedirectRef = useRef<{ route: string; state: TabsState } | null>(null);

  useLayoutEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;
    setIsRestored(true);

    const current = persistedState;

    // Migrate old tabs that lack the `pinned` field
    const migratedTabs = current.tabs.map(t => ({ ...t, pinned: t.pinned ?? false }));
    if (current.tabs.some(t => t.pinned === undefined)) {
      commitState({ ...current, tabs: migratedTabs });
    }

    if (migratedTabs.length === 0) {
      const info = getTabInfoFromRoute(location.pathname);
      const routeAssignment = applyIncomingRoute({}, routeFromLocation(location));
      const newTab: Tab = {
        id: v4(),
        lastAccessed: Date.now(),
        pinned: false,
        title: info.defaultTitle || 'New Tab',
        type: info.type,
        ...routeAssignment,
      };
      const newState: TabsState = { activeTabId: newTab.id, tabs: [newTab] };
      commitCurrentHistoryState(newState);
      if (routeAssignment.route !== routeFromLocation(location))
        initialRedirectRef.current = { route: routeAssignment.route, state: newState };
      return;
    }

    /**
     * A full page load can bring an existing tab to a different kind of page (a home tab pasted
     * over with a document link), so the restored tab takes the new route's type and title, as the
     * route sync effect below would. That effect skips this first location.
     */
    const matchingTab = migratedTabs.find(t => location.pathname.startsWith(t.route.split('?')[0]));
    if (matchingTab) {
      const routeAssignment = applyIncomingRoute(matchingTab, routeFromLocation(location));
      const newState: TabsState = {
        activeTabId: matchingTab.id,
        tabs: updateTab(migratedTabs, matchingTab.id, {
          lastAccessed: Date.now(),
          ...getTabTypeAndTitleForRoute({
            pathname: location.pathname,
            prevPathname: getPathnameFromRoute(matchingTab.route),
            tab: matchingTab,
          }),
          ...routeAssignment,
        }),
      };
      commitCurrentHistoryState(newState);
      if (routeAssignment.route !== routeFromLocation(location))
        initialRedirectRef.current = { route: routeAssignment.route, state: newState };
    } else if (current.activeTabId) {
      const active = migratedTabs.find(t => t.id === current.activeTabId);
      const routeAssignment = applyIncomingRoute(active ?? {}, routeFromLocation(location));
      const newState: TabsState = {
        ...current,
        tabs: updateTab(migratedTabs, current.activeTabId, {
          lastAccessed: Date.now(),
          ...getTabTypeAndTitleForRoute({
            pathname: location.pathname,
            prevPathname: getPathnameFromRoute(active?.route ?? ''),
            tab: active,
          }),
          ...routeAssignment,
        }),
      };
      commitCurrentHistoryState(newState);
      if (routeAssignment.route !== routeFromLocation(location))
        initialRedirectRef.current = { route: routeAssignment.route, state: newState };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const redirect = initialRedirectRef.current;
    if (!redirect) return;

    initialRedirectRef.current = null;
    navigate(redirect.route, {
      replace: true,
      state: createNavigateState(createTabsSnapshot(redirect.state)),
    });
  }, [navigate]);

  // -------------------------------------------------------------------------
  // Browser location -> active tab route sync
  // -------------------------------------------------------------------------

  /**
   * Sync active tab's route when location changes (after initialization).
   * Keyed on the full route (pathname + search + hash) so URL-param updates like
   * `?walkThrough=true` trigger the sync — without this, query-driven features
   * inside tabs never see the updated params.
   * Ephemeral deep-link keys are split into `pendingParams` and stripped from the
   * browser URL via replace navigate so they never persist on `tab.route`.
   * IMPORTANT: Do NOT write `lastAccessed: Date.now()` here — it creates a new
   * object on every pathname change, causing cascading re-renders and title
   * flickering. `lastAccessed` is only updated on explicit user actions
   * (switchToTab, openNewTab, navigateInTab).
   */
  const currentRoute = routeFromLocation(location);
  const prevRouteRef = useRef(currentRoute);
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (currentRoute === prevRouteRef.current) return;
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    const current = stateRef.current;
    if (!current.activeTabId) return;

    const activeTab = current.tabs.find(t => t.id === current.activeTabId);
    const incomingRoute = getRouteWithStickyParams(activeTab?.route, currentRoute);
    const routeAssignment = applyIncomingRoute(activeTab ?? {}, incomingRoute);
    const hasNewEphemeral = Object.keys(splitRouteParams(currentRoute).pendingParams).length > 0;
    /**
     * Skip if durable route already matches and there is nothing ephemeral to claim. A navigation
     * that dropped a sticky param still has to put it back on the URL.
     */
    if (
      activeTab?.route === routeAssignment.route &&
      !hasNewEphemeral &&
      incomingRoute === currentRoute
    ) {
      prevRouteRef.current = currentRoute;
      return;
    }
    prevRouteRef.current = routeAssignment.route;

    const typeAndTitle = getTabTypeAndTitleForRoute({
      pathname: location.pathname,
      prevPathname,
      tab: activeTab,
    });
    desiredTitleRef.current = typeAndTitle.title;
    const newState: TabsState = {
      ...current,
      tabs: updateTab(current.tabs, current.activeTabId, { ...typeAndTitle, ...routeAssignment }),
    };
    /**
     * Tag this history entry with a full tabs snapshot (fallback for navigations
     * not controlled by TabProvider, e.g. direct useNavigate() calls)
     */
    commitCurrentHistoryState(newState);
    if (routeAssignment.route !== currentRoute) {
      navigate(routeAssignment.route, {
        replace: true,
        state: createNavigateState(createTabsSnapshot(newState)),
      });
    }
  }, [commitCurrentHistoryState, currentRoute, location.pathname, navigate]);

  // -------------------------------------------------------------------------
  // Browser Back/Forward restoration
  // -------------------------------------------------------------------------

  // Handle browser back/forward — restore full tab state from history entry
  useEffect(() => {
    function handlePopstate() {
      // Try full snapshot first (new history entries)
      const snapshot = readTabsSnapshotFromHistory();
      if (snapshot) {
        commitState({
          activeTabId: snapshot.activeTabId,
          tabs: snapshot.tabs.map(t => ({
            ...t,
            lastAccessed: t.id === snapshot.activeTabId ? Date.now() : 0,
          })),
        });
        return;
      }

      // Backward compat: old entries that only stored alineTabId
      const historyTabId = readTabIdFromHistory();
      if (!historyTabId) return;

      const current = stateRef.current;
      if (historyTabId !== current.activeTabId) {
        const tab = current.tabs.find(t => t.id === historyTabId);
        if (tab) {
          const routeAssignment = applyIncomingRoute(tab, currentBrowserRoute());
          commitState({
            ...stateRef.current,
            activeTabId: historyTabId,
            tabs: updateTab(stateRef.current.tabs, historyTabId, {
              lastAccessed: Date.now(),
              ...routeAssignment,
            }),
          });
          if (routeAssignment.route !== currentBrowserRoute())
            navigate(routeAssignment.route, { replace: true });
        }
      } else if (current.activeTabId) {
        const active = current.tabs.find(t => t.id === current.activeTabId);
        const routeAssignment = applyIncomingRoute(active ?? {}, currentBrowserRoute());
        commitState({
          ...current,
          tabs: updateTab(current.tabs, current.activeTabId, routeAssignment),
        });
        if (routeAssignment.route !== currentBrowserRoute())
          navigate(routeAssignment.route, { replace: true });
      }
    }

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [commitState, navigate]);

  const activeTab = useMemo(
    () => state.tabs.find(t => t.id === state.activeTabId),
    [state.tabs, state.activeTabId]
  );

  // -------------------------------------------------------------------------
  // Global document title coordination
  // -------------------------------------------------------------------------

  /**
   * Intercept document.title writes so hidden-tab Helmets cannot overwrite the
   * active title. All tabs stay mounted while react-helmet writes to the global
   * document singleton.
   */
  useEffect(() => {
    const desc = Object.getOwnPropertyDescriptor(Document.prototype, 'title');
    if (!desc?.set || !desc?.get) return;

    const originalSet = desc.set;
    const originalGet = desc.get;

    Object.defineProperty(document, 'title', {
      configurable: true,
      get() {
        return originalGet.call(this);
      },
      set(value: string) {
        if (value === desiredTitleRef.current) {
          originalSet.call(this, value);
        }
      },
    });

    return () => {
      // Remove instance property to restore prototype accessor
      Reflect.deleteProperty(document, 'title');
    };
  }, []);

  // Sync page title to active tab
  const activeTabTitle = activeTab?.title;
  useEffect(() => {
    const title = activeTabTitle || 'Aline';
    desiredTitleRef.current = title;
    // Write using the native prototype setter (bypasses our instance-level override)
    const nativeSet = Object.getOwnPropertyDescriptor(Document.prototype, 'title')?.set;
    nativeSet?.call(document, title);
  }, [activeTabTitle]);

  // -------------------------------------------------------------------------
  // Tab operations
  // -------------------------------------------------------------------------

  const openNewTab = useCallback(
    (type: TabType, title: string, route: string) => {
      const current = stateRef.current;
      const routeAssignment = applyIncomingRoute({}, route);
      const newTab: Tab = {
        id: v4(),
        lastAccessed: Date.now(),
        pinned: false,
        title,
        type,
        ...routeAssignment,
      };
      const updatedTabs = [...current.tabs, newTab];
      const newState: TabsState = { activeTabId: newTab.id, tabs: updatedTabs };
      commitState(newState);
      navigate(routeAssignment.route, { state: createNavigateState(createTabsSnapshot(newState)) });

      // Soft limit warning at 20+ tabs (once per session)
      if (updatedTabs.length > 20 && !tabLimitWarningShownRef.current) {
        tabLimitWarningShownRef.current = true;
        dispatchTabLimitWarning();
      }
    },
    [commitState, dispatchTabLimitWarning, navigate]
  );

  const navigateInTab = useCallback(
    (type: TabType, title: string, route: string) => {
      const sourceTabId = stateRef.current.activeTabId;
      if (!sourceTabId) return;

      const current = stateRef.current;
      if (!current.activeTabId) return;

      // Full in-tab navigation replaces content — drop any prior document's pending params.
      const activeTab = current.tabs.find(t => t.id === current.activeTabId);
      const routeAssignment = applyIncomingRoute(
        {},
        getRouteWithStickyParams(activeTab?.route, route)
      );

      /**
       * Always navigate within the current active tab (replace its content).
       *  Like VS Code: clicking in the explorer replaces the current editor.
       */
      const newState: TabsState = {
        ...current,
        tabs: updateTab(current.tabs, current.activeTabId, {
          lastAccessed: Date.now(),
          title,
          type,
          ...routeAssignment,
        }),
      };
      commitState(newState);
      navigate(routeAssignment.route, { state: createNavigateState(createTabsSnapshot(newState)) });
    },
    [commitState, navigate]
  );

  const switchToTab = useCallback(
    (tabId: UUID, routeOverride?: string) => {
      const current = stateRef.current;
      const tab = current.tabs.find(t => t.id === tabId);
      if (!tab) return;

      const routeAssignment = routeOverride
        ? applyIncomingRoute(tab, routeOverride)
        : { pendingParams: tab.pendingParams, route: tab.route };
      const newState: TabsState = {
        activeTabId: tabId,
        tabs: updateTab(current.tabs, tabId, { lastAccessed: Date.now(), ...routeAssignment }),
      };
      commitState(newState);
      navigate(routeAssignment.route, { state: createNavigateState(createTabsSnapshot(newState)) });
    },
    [commitState, navigate]
  );

  const consumePendingParam = useCallback((tabId: UUID, key: string): string | null => {
    const pendingParams = pendingByTabIdRef.current[tabId];
    const value = pendingParams?.[key];
    if (value === undefined) return null;

    const remaining = omitPendingParam(pendingParams, key);
    const nextPending = remaining
      ? { ...pendingByTabIdRef.current, [tabId]: remaining }
      : omit([tabId], pendingByTabIdRef.current);
    pendingByTabIdRef.current = nextPending;
    setPendingByTabId(nextPending);
    return value;
  }, []);

  const closeTab = useCallback(
    (tabId: UUID) => {
      const current = stateRef.current;
      const tabIndex = current.tabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return;

      // Pinned tabs cannot be closed
      if (current.tabs[tabIndex].pinned) return;

      const remaining = current.tabs.filter(t => t.id !== tabId);
      const wasActive = current.activeTabId === tabId;

      function closeCurrentTab() {
        const latest = stateRef.current;
        const latestTabIndex = latest.tabs.findIndex(t => t.id === tabId);
        if (latestTabIndex === -1) return;
        if (latest.tabs[latestTabIndex].pinned) return;

        const latestRemaining = latest.tabs.filter(t => t.id !== tabId);
        const latestWasActive = latest.activeTabId === tabId;

        if (latestRemaining.length === 0) {
          const homeTab: Tab = {
            id: v4(),
            lastAccessed: Date.now(),
            pinned: false,
            route: DEFAULT_TAB_ROUTE,
            title: 'Home',
            type: TAB_TYPES.home,
          };
          const newState: TabsState = { activeTabId: homeTab.id, tabs: [homeTab] };
          commitState(newState);
          navigate(DEFAULT_TAB_ROUTE, {
            state: createNavigateState(createTabsSnapshot(newState)),
          });
          return;
        }

        if (latestWasActive) {
          const nextIndex = Math.min(latestTabIndex, latestRemaining.length - 1);
          const nextTab = latestRemaining[nextIndex];
          const newState: TabsState = { activeTabId: nextTab.id, tabs: latestRemaining };
          commitState(newState);
          navigate(nextTab.route, {
            state: createNavigateState(createTabsSnapshot(newState)),
          });
        } else {
          commitCurrentHistoryState({ ...latest, tabs: latestRemaining });
        }
      }

      if (wasActive) {
        closeCurrentTab();
        return;
      }

      commitCurrentHistoryState({ ...current, tabs: remaining });
    },
    [commitCurrentHistoryState, navigate, commitState]
  );

  const updateActiveTabTitle = useCallback(
    (title: string) => {
      const current = stateRef.current;
      if (!current.activeTabId) return;

      // Update ref immediately so Helmet's RAF write (with the same title) passes through
      desiredTitleRef.current = title;
      commitCurrentHistoryState({
        ...current,
        tabs: updateTab(current.tabs, current.activeTabId, { title }),
      });
    },
    [commitCurrentHistoryState]
  );

  const reorderTab = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = stateRef.current;
      if (fromIndex === toIndex) return;
      // Prevent dragging across the pinned/unpinned boundary
      if (current.tabs[fromIndex].pinned !== current.tabs[toIndex].pinned) return;
      const reordered = [...current.tabs];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      commitCurrentHistoryState({ ...current, tabs: reordered });
    },
    [commitCurrentHistoryState]
  );

  const setTabOrder = useCallback(
    (orderedIds: UUID[]) => {
      const current = stateRef.current;
      const tabMap = new Map(current.tabs.map(t => [t.id, t]));
      const reordered = orderedIds.flatMap(id => {
        const tab = tabMap.get(id);
        return tab ? [tab] : [];
      });
      if (reordered.length === current.tabs.length)
        commitCurrentHistoryState({ ...current, tabs: reordered });
    },
    [commitCurrentHistoryState]
  );

  const togglePinTab = useCallback(
    (tabId: UUID) => {
      const current = stateRef.current;
      const tabIndex = current.tabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return;

      const nowPinned = !current.tabs[tabIndex].pinned;
      const updated = updateTab(current.tabs, tabId, { pinned: nowPinned });

      // Re-sort: pinned tabs left, unpinned right, preserving relative order within each group
      const pinned = updated.filter(t => t.pinned);
      const unpinned = updated.filter(t => !t.pinned);
      commitCurrentHistoryState({ ...current, tabs: [...pinned, ...unpinned] });
    },
    [commitCurrentHistoryState]
  );

  const closeOtherTabs = useCallback(
    (tabId: UUID) => {
      const current = stateRef.current;
      if (!current.tabs.some(t => t.id === tabId)) return;

      // Keep the target tab + all pinned tabs
      const remaining = current.tabs.filter(t => t.id === tabId || t.pinned);
      const activeStillExists = remaining.some(t => t.id === current.activeTabId);
      const newActiveId = activeStillExists ? current.activeTabId : tabId;

      const newState: TabsState = { activeTabId: newActiveId, tabs: remaining };

      if (newActiveId !== current.activeTabId) {
        commitState(newState);
        const newActive = remaining.find(t => t.id === newActiveId);
        if (newActive) {
          navigate(newActive.route, {
            state: createNavigateState(createTabsSnapshot(newState)),
          });
        }
      } else {
        commitCurrentHistoryState(newState);
      }
    },
    [commitCurrentHistoryState, navigate, commitState]
  );

  const closeTabsToLeft = useCallback(
    (tabId: UUID) => {
      const current = stateRef.current;
      const tabIndex = current.tabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return;

      // Keep target tab + tabs to the right + any pinned tabs to the left
      const remaining = current.tabs.filter((t, i) => i >= tabIndex || t.pinned);
      const activeStillExists = remaining.some(t => t.id === current.activeTabId);
      const newActiveId = activeStillExists ? current.activeTabId : remaining[0].id;

      const newState: TabsState = { activeTabId: newActiveId, tabs: remaining };

      if (newActiveId !== current.activeTabId) {
        commitState(newState);
        const newActive = remaining.find(t => t.id === newActiveId);
        if (newActive) {
          navigate(newActive.route, {
            state: createNavigateState(createTabsSnapshot(newState)),
          });
        }
      } else {
        commitCurrentHistoryState(newState);
      }
    },
    [commitCurrentHistoryState, navigate, commitState]
  );

  const closeTabsToRight = useCallback(
    (tabId: UUID) => {
      const current = stateRef.current;
      const tabIndex = current.tabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return;

      // Keep tabs up to and including target + any pinned tabs to the right
      const remaining = current.tabs.filter((t, i) => i <= tabIndex || t.pinned);
      const activeStillExists = remaining.some(t => t.id === current.activeTabId);
      const newActiveId = activeStillExists
        ? current.activeTabId
        : remaining[remaining.length - 1].id;

      const newState: TabsState = { activeTabId: newActiveId, tabs: remaining };

      if (newActiveId !== current.activeTabId) {
        commitState(newState);
        const newActive = remaining.find(t => t.id === newActiveId);
        if (newActive) {
          navigate(newActive.route, {
            state: createNavigateState(createTabsSnapshot(newState)),
          });
        }
      } else {
        commitCurrentHistoryState(newState);
      }
    },
    [commitCurrentHistoryState, navigate, commitState]
  );

  // -------------------------------------------------------------------------
  // Global keyboard shortcuts
  // -------------------------------------------------------------------------

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      for (const key of tabHotkeyKeys) {
        const hotkey = tabHotkeys[key];
        if (hotkey.action && hotkey.test?.(event)) {
          event.preventDefault();
          hotkey.action({ closeTab, event, openNewTab, state: stateRef.current, switchToTab });
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeTab, openNewTab, switchToTab]);

  // -------------------------------------------------------------------------
  // Custom event handler
  // -------------------------------------------------------------------------

  useEffect(() => {
    function handleOpenInAppTab(event: CustomEvent<OpenInAppTabOptions>) {
      event.preventDefault();
      const {
        applyRouteToExistingTab = false,
        newTab = true,
        preferExistingTab = true,
        route,
        title,
      } = event.detail;

      const sourceTabId = stateRef.current.activeTabId;
      const { tabs } = stateRef.current;

      if (preferExistingTab) {
        // Check if a tab with the same resource is already open (e.g., /doc/abc matches /doc/abc/ai)
        const existingTab = tabs.find(t => getRoutesMatchSameResource(t.route, route));

        if (existingTab && (existingTab.id !== sourceTabId || applyRouteToExistingTab)) {
          switchToTab(existingTab.id, applyRouteToExistingTab ? route : undefined);
          return;
        }

        /**
         * The resource is already in the active tab. A new-tab request has nothing to do, since
         * opening one would duplicate the tab in view. An in-tab request still has to move: another
         * view of the same resource (`/doc/abc` to `/doc/abc/ai`) is a route change the tab has not
         * made yet, so it falls through to `navigateInTab`.
         */
        if (existingTab && newTab) return;
      }

      const info = getTabInfoFromRoute(route);
      const method = newTab ? openNewTab : navigateInTab;
      method(info.type, title || info.defaultTitle || 'New Tab', route);
    }

    window.addEventListener(OPEN_IN_APP_TAB_EVENT, handleOpenInAppTab);
    return () => window.removeEventListener(OPEN_IN_APP_TAB_EVENT, handleOpenInAppTab);
  }, [closeTab, navigateInTab, openNewTab, switchToTab]);

  const value = useMemo(
    () => ({
      activeTab,
      consumePendingParam,
      internalMethods: {
        closeOtherTabs,
        closeTab,
        closeTabsToLeft,
        closeTabsToRight,
        navigateInTab,
        openNewTab,
        reorderTab,
        setTabOrder,
        switchToTab,
        togglePinTab,
      },
      tabs: state.tabs,
      updateActiveTabTitle,
    }),
    [
      activeTab,
      closeOtherTabs,
      closeTab,
      closeTabsToLeft,
      closeTabsToRight,
      consumePendingParam,
      navigateInTab,
      openNewTab,
      reorderTab,
      setTabOrder,
      switchToTab,
      state.tabs,
      togglePinTab,
      updateActiveTabTitle,
    ]
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}
